import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

/**
 * Per-lane "new message" signal for the workspace rail.
 *
 * Live messages already arrive over SSE and land in `state.threads.liveMessages`,
 * keyed by threadId — but nothing surfaced them on the lane buttons. An operator
 * reading a long conversation had no way to tell WHICH other lane just received
 * something: the sound played and the bell counted it, neither of which says
 * "open Transport".
 *
 * A lane is unread when messages have arrived in one of its threads since the
 * operator last had that lane open. Opening the lane marks it seen.
 */

/** threadIds belonging to each lane, derived from the quote. */
export function laneThreadIds(quote) {
  if (!quote) return {};
  const ids = (arr, key) =>
    (arr || []).map((x) => x?.[key]).filter((t) => typeof t === "string" && t);

  return {
    CustomerConversation: [quote.gmailThreadId].filter(Boolean),
    AirlineConversation: ids(quote.my_saved_airlines, "emailThreadId"),
    TransportConversation: ids(quote.my_saved_agents, "emailThreadId"),
    ShipperConversation: [
      quote.shipperDetails?.shipperThreadId,
      quote.shipperDetails?.emailThreadId,
    ].filter(Boolean),
  };
}

/**
 * @param {object} quote the loaded quote
 * @param {string} activeLaneKey the lane currently open
 * @returns {{unreadByLane: Record<string, number>, markLaneSeen: (k:string)=>void}}
 */
export function useLaneUnread(quote, activeLaneKey) {
  const liveMessages = useSelector((s) => s.threads?.liveMessages || {});
  const threadsByLane = useMemo(() => laneThreadIds(quote), [quote]);

  // How many messages each thread had when its lane was last open. A ref, not
  // state: updating it must not itself trigger a render pass.
  const seenRef = useRef({});
  const [, forceTick] = useState(0);

  const countFor = (laneKey) =>
    (threadsByLane[laneKey] || []).reduce(
      (total, tid) => total + (liveMessages[tid]?.length || 0),
      0
    );

  const markLaneSeen = (laneKey) => {
    const threads = threadsByLane[laneKey] || [];
    if (threads.length === 0) return;
    let changed = false;
    for (const tid of threads) {
      const now = liveMessages[tid]?.length || 0;
      if (seenRef.current[tid] !== now) {
        seenRef.current[tid] = now;
        changed = true;
      }
    }
    if (changed) forceTick((n) => n + 1);
  };

  // The open lane is by definition read — including messages that land while
  // the operator is sitting in it.
  useEffect(() => {
    if (activeLaneKey) markLaneSeen(activeLaneKey);
  }, [activeLaneKey, liveMessages]);

  const unreadByLane = useMemo(() => {
    const out = {};
    for (const laneKey of Object.keys(threadsByLane)) {
      const unread = (threadsByLane[laneKey] || []).reduce((total, tid) => {
        const arrived = liveMessages[tid]?.length || 0;
        const seen = seenRef.current[tid] ?? 0;
        // A thread buffer is capped and can shrink; never report negative.
        return total + Math.max(0, arrived - seen);
      }, 0);
      if (unread > 0) out[laneKey] = unread;
    }
    return out;
  }, [threadsByLane, liveMessages, activeLaneKey]);

  return { unreadByLane, markLaneSeen, countFor };
}
